import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/ClosureApprovalDashboard.jsx
// HO checker dashboard for approving, rejecting, and reopening branch EOD submissions.
// Branch users see an access-denied message — they never interact with this page.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Typography, Spin, Alert,
  Row, Col, Select, DatePicker, Modal, Input, Tooltip, Badge, Statistic,
} from 'antd';
import {
  CheckOutlined, CloseOutlined, ReloadOutlined,
  RedoOutlined, LockOutlined, SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import {
  fetchClosureApprovals,
  approveClosureRequest,
  rejectClosureRequest,
  reopenClosedDay,
} from '../api';
import { DAY_STATUS, DAY_STATUS_OPTIONS } from '../constants/treasuryConstants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const STATUS_CONFIG = {
  [DAY_STATUS.PENDING_CHECKER_APPROVAL]: { color: 'blue',    label: 'Pending Approval' },
  [DAY_STATUS.CLOSED]:                  { color: 'default', label: 'Closed' },
  [DAY_STATUS.REOPENED]:                { color: 'purple',  label: 'Reopened' },
  [DAY_STATUS.PENDING_CASH_VERIFICATION]: { color: 'orange', label: 'Pending Verification' },
  [DAY_STATUS.OPEN]:                    { color: 'green',   label: 'Open' },
};

// Status options shown in the filter
const FILTER_STATUS_OPTIONS = [
  { value: DAY_STATUS.PENDING_CHECKER_APPROVAL, label: 'Pending Approval' },
  { value: DAY_STATUS.CLOSED,                   label: 'Closed' },
  { value: DAY_STATUS.REOPENED,                 label: 'Reopened' },
  { value: DAY_STATUS.PENDING_CASH_VERIFICATION, label: 'Pending Verification' },
];

export default function ClosureApprovalDashboard() {
  const { user } = useAuth();
  const userId = user?.id || user?._id;

  const [isHO, setIsHO] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [branches, setBranches] = useState([]);
  const [initLoading, setInitLoading] = useState(true);

  const [filters, setFilters] = useState({
    status: null, // we will set default based on user role
    branchId: null,
    dateRange: null,
  });

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reject modal
  const [rejectModal, setRejectModal] = useState({ open: false, dayId: null, reason: '' });
  const [rejectLoading, setRejectLoading] = useState(false);

  // Reopen modal
  const [reopenModal, setReopenModal] = useState({ open: false, dayId: null, reason: '' });
  const [reopenLoading, setReopenLoading] = useState(false);

  // Per-row approve loading
  const [approvingId, setApprovingId] = useState(null);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);
        setIsAdmin(isGlobalViewer(user));

        if (ho) {
          const bRes = await fetchBranches({ limit: 100 });
          setBranches(bRes?.data?.data || bRes?.data || []);
        }

        setFilters(f => ({
          ...f,
          status: DAY_STATUS.PENDING_CHECKER_APPROVAL,
        }));
      } catch {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchList = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: pagination.pageSize };
      if (filters.status) params.status = filters.status;
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD');

      const res = await fetchClosureApprovals(params);
      const data = res?.data;
      setRows(data?.data || []);
      setPagination(prev => ({
        ...prev,
        current: data?.pagination?.current || 1,
        total: data?.pagination?.total || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load closure approvals');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize]);

  useEffect(() => {
    if (!initLoading && filters.status) fetchList(1);
  }, [filters, initLoading]);

  // ── Approve ────────────────────────────────────────────────────────────────
  const handleApprove = async (id) => {
    setApprovingId(id);
    try {
      await approveClosureRequest(id);
      fetchList(pagination.current);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Approval failed');
    } finally {
      setApprovingId(null);
    }
  };

  // ── Reject ─────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!rejectModal.reason.trim()) return;
    setRejectLoading(true);
    try {
      await rejectClosureRequest(rejectModal.dayId, { rejectionReason: rejectModal.reason });
      setRejectModal({ open: false, dayId: null, reason: '' });
      fetchList(pagination.current);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Rejection failed');
    } finally {
      setRejectLoading(false);
    }
  };

  // ── Reopen ─────────────────────────────────────────────────────────────────
  const handleReopen = async () => {
    if (!reopenModal.reason.trim()) return;
    setReopenLoading(true);
    try {
      await reopenClosedDay(reopenModal.dayId, { reopenReason: reopenModal.reason });
      setReopenModal({ open: false, dayId: null, reason: '' });
      fetchList(pagination.current);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Reopen failed');
    } finally {
      setReopenLoading(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Date',
      dataIndex: 'businessDate',
      width: 110,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => new Date(a.businessDate) - new Date(b.businessDate),
    },
    {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      width: 150,
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{row.branch?.name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.branch?.code}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 160,
      render: (v) => {
        const cfg = STATUS_CONFIG[v] || {};
        return <Tag color={cfg.color}>{cfg.label || v}</Tag>;
      },
    },
    {
      title: 'Physical Cash',
      dataIndex: ['denominationCount', 'totalPhysicalCash'],
      width: 130,
      align: 'right',
      render: (v, row) => `₹${fmt(row.denominationCount?.totalPhysicalCash || 0)}`,
    },
    {
      title: 'Expected Cash',
      dataIndex: 'expectedCash',
      width: 130,
      align: 'right',
      render: (v) => `₹${fmt(v || 0)}`,
    },
    {
      title: 'Difference',
      dataIndex: 'cashDifference',
      width: 110,
      align: 'right',
      render: (v) => {
        if (v === 0) return <Text style={{ color: '#389e0d', fontWeight: 500 }}>₹0</Text>;
        return (
          <Text style={{ color: '#cf1322', fontWeight: 500 }}>
            {v > 0 ? '+' : ''}₹{fmt(Math.abs(v || 0))}
          </Text>
        );
      },
    },
    {
      title: 'Submitted By',
      dataIndex: ['submittedBy', 'name'],
      width: 130,
      render: (v, row) => row.submittedBy?.name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Submitted At',
      dataIndex: 'submittedForApprovalAt',
      width: 130,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">—</Text>,
    },
    {
      title: 'Stage',
      dataIndex: 'currentStepName',
      width: 130,
      render: (v, row) => row.status === DAY_STATUS.PENDING_CHECKER_APPROVAL
        ? (v || <Text type="secondary">—</Text>)
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, row) => {
        const isClosed = row.status === DAY_STATUS.CLOSED;

        const canApprove = row.status === DAY_STATUS.PENDING_CHECKER_APPROVAL
          && Array.isArray(row.pendingApproverIds)
          && row.pendingApproverIds.some((a) => String(a) === String(userId));

        return (
          <Space>
            {canApprove && (
              <>
                <Tooltip title="Approve">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    loading={approvingId === row._id}
                    onClick={() => handleApprove(row._id)}
                  />
                </Tooltip>
                <Tooltip title="Reject">
                  <Button
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setRejectModal({ open: true, dayId: row._id, reason: '' })}
                  />
                </Tooltip>
              </>
            )}
            {isClosed && isAdmin && (
              <Tooltip title="Reopen (admin)">
                <Button
                  size="small"
                  icon={<RedoOutlined />}
                  onClick={() => setReopenModal({ open: true, dayId: row._id, reason: '' })}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const isMyPendingRow = (row) => row.status === DAY_STATUS.PENDING_CHECKER_APPROVAL
    && Array.isArray(row.pendingApproverIds)
    && row.pendingApproverIds.some((a) => String(a) === String(userId));

  const pendingCount = rows.filter(isMyPendingRow).length;

  return (
    <div style={{ padding: '16px 0' }}>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="baseline">
            <Title level={5} style={{ margin: 0 }}>
              <SettingOutlined style={{ marginRight: 8 }} />
              Closure Approvals
            </Title>
            {filters.status === DAY_STATUS.PENDING_CHECKER_APPROVAL && pendingCount > 0 && (
              <Badge count={pendingCount} style={{ backgroundColor: '#1677ff' }} />
            )}
          </Space>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={() => fetchList(1)} loading={loading}>
            Refresh
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />
      )}

      {/* ── Filters ── */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} sm={8}>
            <Select
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(v) => setFilters(f => ({ ...f, status: v }))}
              options={FILTER_STATUS_OPTIONS}
              placeholder="Filter by status"
              allowClear
            />
          </Col>
          {isHO && (
            <Col xs={24} sm={7}>
              <Select
                placeholder="All branches"
                allowClear
                showSearch
                style={{ width: '100%' }}
                value={filters.branchId}
                onChange={(v) => setFilters(f => ({ ...f, branchId: v || null }))}
                optionFilterProp="label"
                options={branches.map(b => ({ value: b._id, label: `${b.name} (${b.code})` }))}
              />
            </Col>
          )}
          <Col xs={24} sm={9}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters(f => ({ ...f, dateRange: dates }))}
              format="DD/MM/YYYY"
            />
          </Col>
        </Row>
      </Card>

      {/* ── Table ── */}
      <Table
        dataSource={rows}
        columns={columns}
        rowKey="_id"
        loading={loading}
        size="small"
        scroll={{ x: 1200 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (t) => `${t} records`,
          onChange: (page) => fetchList(page),
        }}
        rowClassName={(row) => (isMyPendingRow(row) ? 'ant-table-row-pending' : '')}
        locale={{ emptyText: 'No closure records found' }}
      />

      {/* ── Reject Modal ── */}
      <Modal
        title="Reject EOD Submission"
        open={rejectModal.open}
        onOk={handleReject}
        onCancel={() => setRejectModal({ open: false, dayId: null, reason: '' })}
        okText="Reject"
        okButtonProps={{ danger: true, disabled: !rejectModal.reason.trim(), loading: rejectLoading }}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          The branch will be sent back to denomination re-entry. Provide a reason so the branch knows what to correct.
        </Text>
        <TextArea
          rows={3}
          placeholder="Enter rejection reason..."
          value={rejectModal.reason}
          onChange={(e) => setRejectModal(m => ({ ...m, reason: e.target.value }))}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* ── Reopen Modal ── */}
      <Modal
        title="Reopen Closed Business Day"
        open={reopenModal.open}
        onOk={handleReopen}
        onCancel={() => setReopenModal({ open: false, dayId: null, reason: '' })}
        okText="Reopen"
        okButtonProps={{ disabled: !reopenModal.reason.trim(), loading: reopenLoading }}
        destroyOnClose
      >
        <Alert
          type="warning"
          message="This is an admin-only action. Reopening allows the branch to record additional transactions for this date."
          style={{ marginBottom: 12 }}
        />
        <TextArea
          rows={3}
          placeholder="Enter reason for reopening..."
          value={reopenModal.reason}
          onChange={(e) => setReopenModal(m => ({ ...m, reason: e.target.value }))}
          maxLength={500}
          showCount
        />
      </Modal>
    </div>
  );
}
